import { useState, useEffect, useMemo, useRef } from "react";
import Layout from "../components/Layout";
import Pagination, { useClientPagination } from "../components/Pagination";
import PageForm from "../components/PageForm";
import WizardPopover from "../components/WizardPopover";
import TechnicianAssignment from "../components/TechnicianAssignment";
import BranchHierarchyPicker from "../components/BranchHierarchyPicker";
import JornadaPicker from "../components/JornadaPicker";
import CuadrillaContratista, { CuadrillasImportar } from "../components/CuadrillaContratista";   // v101.205.0
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
// v101.27.0 · «Entrar como» (solo super_admin)
import ImpersonateModal from "../components/ImpersonateModal";
import { isImpersonating } from "../lib/impersonation";
import {
  Users,
  Plus,
  Search,
  Loader2,
  ShieldCheck,
  ShieldOff,
  Mail,
  UserCog,
  X,
  Unlock,
  KeyRound,
  Pencil,
  Filter,
  Upload,
  Download,
  Eye,
  EyeOff,
  Trash2,
  AlertTriangle,
  User,
  Shield,
  MapPin,
  Phone,
  Sparkles,
  CheckCircle2,
  LogIn,
} from "lucide-react";

// v99.5.0 · Modo Austeridad Corrección 3: Solo roles de campo tienen Bodega/Zonas
const FIELD_ROLES = ["technician", "contractor"];

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-800",
  admin_zone: "bg-indigo-100 text-indigo-800",
  supervisor: "bg-blue-100 text-blue-800",
  fiscalizer: "bg-amber-100 text-amber-800",
  finance: "bg-green-100 text-green-800",
  technician: "bg-gray-100 text-gray-800",
  contractor: "bg-orange-100 text-orange-800",
  support: "bg-cyan-100 text-cyan-800",
  auditor: "bg-pink-100 text-pink-800",
};

export default function AdminUsuarios() {
  const { hasPerm, refreshPerms, user: currentUser } = useAuth();
  const [impTarget, setImpTarget] = useState(null); // v101.27.0
  const [users, setUsers] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [roles, setRoles] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // v99.7.0 · "" | "active" | "inactive"
  const [branchFilter, setBranchFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  // v6.3.3 · Selección masiva
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // null | "reset" | "role" | "delete"
  const [bulkNewRole, setBulkNewRole] = useState("technician");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResetResults, setBulkResetResults] = useState(null); // [{email, temporary_password}]
  const [showCreate, setShowCreate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingFull, setEditingFull] = useState(false);   // true → show full PageForm; false → popover
  const editAnchorRef = useRef(null);
  const [branches, setBranches] = useState([]);
  // v101.6.0 · return_warehouse_location_id = bodega de devoluciones (misma fuente que bodega de materiales)
  const emptyForm = { email: "", name: "", password: "", role: "technician", branch_id: "", warehouse_location_id: "", return_warehouse_location_id: "", zone_ids: [], phone: "" };
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({ name: "", role: "", branch_id: "", warehouse_location_id: "", return_warehouse_location_id: "", zone_ids: [], phone: "", active: true });
  const [creating, setCreating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // v2.7.1 · Auto-asignación de bodegas a técnicos
  const [showAutoAssign, setShowAutoAssign] = useState(false);
  const [autoAssignRunning, setAutoAssignRunning] = useState(false);
  const [autoAssignApplying, setAutoAssignApplying] = useState(false);
  const [autoAssignResult, setAutoAssignResult] = useState(null); // { stats, dry_run }
  const [minScore, setMinScore] = useState(0.75);

  // v2.9.0 · Reset de contraseña por admin (muestra clave temporal en modal)
  const [resetTarget, setResetTarget] = useState(null);      // user objetivo para confirmar
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);      // { email, temporary_password }
  const [showTempPwd, setShowTempPwd] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, r, z, b] = await Promise.all([
        api.get("/v1/users"),
        api.get("/v1/users/roles"),
        api.get("/v1/config/zones"),
        api.get("/v1/config/branches"),
      ]);
      setUsers(u.data.items || []);
      setRoles(r.data || []);
      setZones(z.data.items || []);
      setBranches(b.data.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = users.filter((u) => {
    const email = (u.email || "").toLowerCase();
    const name = (u.name || "").toLowerCase();
    const ql = q.toLowerCase();
    const matchesText = !q || email.includes(ql) || name.includes(ql);
    const matchesRole = !roleFilter || u.role === roleFilter;
    const matchesBranch = !branchFilter || u.branch_id === branchFilter;
    const matchesZone = !zoneFilter || (u.zone_ids || []).includes(zoneFilter) || u.zone_id === zoneFilter;
    const matchesStatus = !statusFilter || (statusFilter === "active" ? u.active !== false : u.active === false);
    return matchesText && matchesRole && matchesBranch && matchesZone && matchesStatus;
  });

  // Paginación cliente (10/25/50/100)
  const pg = useClientPagination(filtered, 10);

  const [showCreateFull, setShowCreateFull] = useState(false);
  const createAnchorRef = useRef(null);

  // v99.5.0 · Cambio de rol en form de creación: limpia bodega/zonas si no es campo
  const handleCreateRoleChange = (newRole) => {
    if (FIELD_ROLES.includes(newRole)) {
      setForm((f) => ({ ...f, role: newRole }));
    } else {
      setForm((f) => ({ ...f, role: newRole, warehouse_location_id: "", return_warehouse_location_id: "", zone_ids: [], branch_id: "" }));
    }
  };

  // v99.5.0 · Cambio de rol en form de edición: limpia bodega/zonas con confirmación
  const handleEditRoleChange = (newRole) => {
    const hadFieldData = editForm.warehouse_location_id || editForm.return_warehouse_location_id || (editForm.zone_ids && editForm.zone_ids.length > 0);
    const wasField = FIELD_ROLES.includes(editForm.role);
    const isField = FIELD_ROLES.includes(newRole);

    if (wasField && !isField && hadFieldData) {
      if (!window.confirm("Al cambiar a un rol no-campo se borrarán la Bodega y Zonas asignadas. ¿Continuar?")) return;
      setEditForm((f) => ({ ...f, role: newRole, warehouse_location_id: "", return_warehouse_location_id: "", zone_ids: [], branch_id: "" }));
    } else if (!isField) {
      setEditForm((f) => ({ ...f, role: newRole, warehouse_location_id: "", return_warehouse_location_id: "", zone_ids: [] }));
    } else {
      setEditForm((f) => ({ ...f, role: newRole }));
    }
  };

  const openCreateModal = (anchorEl) => {
    createAnchorRef.current = anchorEl || null;
    setForm(emptyForm);       // reset ANTES de abrir (fix bug #4)
    setShowPassword(false);
    setShowCreateFull(false);
    setShowCreate(true);
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    setShowCreateFull(false);
    setShowPassword(false);
    setForm(emptyForm);
    setCreating(false);       // safety net (fix bug #1)
    createAnchorRef.current = null;
  };

  const createUser = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (creating) return;     // evita doble submit
    setCreating(true);
    try {
      // v101.6.0 · Sanitizar bodegas vacías → null (igual que saveEdit)
      const payload = { ...form };
      for (const k of ["warehouse_location_id", "return_warehouse_location_id", "branch_id", "zone_id"]) {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
      }
      await api.post("/v1/users", payload);
      toast.success("Usuario creado");
      closeCreateModal();
      await fetchAll();
    } catch (err) {
      // v97.6.0 · El backend puede devolver detail como lista (validación 422): mostrar texto legible
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((x) => `${(x.loc || []).slice(-1)[0] || "campo"}: ${x.msg}`).join(" · ")
        : (d && d.message) || "Error al crear usuario";
      toast.error(msg);
    } finally {
      setCreating(false);     // SIEMPRE liberar (fix bug #1)
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/v1/users/${u.id}`, { active: !u.active });
      toast.success(`Usuario ${u.active ? "desactivado" : "activado"}`);
      await fetchAll();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  const unlockUser = async (u) => {
    try {
      await api.post(`/v1/users/${u.id}/unlock`);
      toast.success("Cuenta desbloqueada");
      await fetchAll();
    } catch (e) {
      toast.error("Error al desbloquear");
    }
  };

  // v2.9.0 · Resetear contraseña desde panel admin — genera clave random en modal.
  // Se muestra UNA SOLA VEZ al admin para que la copie y comparta por canal seguro.
  const resetPassword = (u) => {
    setResetResult(null);
    setShowTempPwd(false);
    setResetTarget(u);
  };

  const confirmResetPassword = async () => {
    if (!resetTarget) return;
    setResetLoading(true);
    try {
      const { data } = await api.post(`/v1/users/${resetTarget.id}/reset-password`);
      setResetResult({
        email: data.email || resetTarget.email,
        temporary_password: data.temporary_password,
      });
      toast.success(`Clave temporal generada para ${resetTarget.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al resetear la contraseña");
      setResetTarget(null);
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetModal = () => {
    setResetTarget(null);
    setResetResult(null);
    setShowTempPwd(false);
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      toast.success("Clave copiada al portapapeles");
    } catch {
      toast.error("No se pudo copiar (permítelo en el navegador)");
    }
  };

  const openEdit = (u, anchorEl) => {
    editAnchorRef.current = anchorEl || null;
    setEditingUser(u);
    setEditingFull(false);   // start as popover
    setEditForm({
      name: u.name || "",
      role: u.role || "",
      branch_id: u.branch_id || "",
      warehouse_location_id: u.warehouse_location_id || "",
      return_warehouse_location_id: u.return_warehouse_location_id || "",
      zone_ids: u.zone_ids || (u.zone_id ? [u.zone_id] : []),
      phone: u.phone || "",
      active: u.active !== false,
    });
  };

  const closeEdit = () => {
    setEditingUser(null);
    setEditingFull(false);
    editAnchorRef.current = null;
  };

  const saveEdit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSavingEdit(true);
    try {
      // v2.7.1 · Sanitizar payload: convertir cadenas vacías a null y eliminar
      // campos undefined para evitar errores 422 de Pydantic.
      const payload = { ...editForm };
      for (const k of ["warehouse_location_id", "return_warehouse_location_id", "branch_id", "zone_id"]) {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
      }
      // v101.8.0 · Las zonas son de «Gestión Geográfica»: no se envían desde aquí
      delete payload.zone_ids;
      delete payload.zone_id;
      await api.patch(`/v1/users/${editingUser.id}`, payload);
      toast.success("Usuario actualizado");
      closeEdit();
      await fetchAll();
      // Si el usuario editado es el actualmente logueado, refrescar sus permisos
      // (rol, zonas, sucursal) para reflejar los cambios inmediatamente en la UI.
      if (editingUser?.id === currentUser?.id) {
        try { await refreshPerms(); } catch { /* no bloquear */ }
      }
    } catch (err) {
      // v2.7.1 · Renderizar errores de validación de Pydantic (array de {loc, msg}).
      let msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map((e) => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join(" · ");
      }
      toast.error(msg || err?.message || "Error al guardar");
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteConfirm = (u) => {
    setDeletingUser(u);
  };

  const closeDeleteConfirm = () => {
    if (confirmingDelete) return;
    setDeletingUser(null);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser || confirmingDelete) return;
    setConfirmingDelete(true);
    try {
      const { data } = await api.delete(`/v1/users/${deletingUser.id}`);
      toast.success(`Usuario ${data.deleted_email || deletingUser.email} eliminado permanentemente`);
      setDeletingUser(null);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setConfirmingDelete(false);
    }
  };

  // v2.7.1 · Auto-asignación de bodegas
  const openAutoAssign = () => {
    setAutoAssignResult(null);
    setShowAutoAssign(true);
  };
  const runAutoAssignDryRun = async () => {
    setAutoAssignRunning(true);
    try {
      const { data } = await api.post(`/admin/technicians/auto-assign-warehouses?dry_run=true&min_score=${minScore}`);
      setAutoAssignResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error ejecutando previsualización");
    } finally {
      setAutoAssignRunning(false);
    }
  };
  const applyAutoAssign = async () => {
    if (!autoAssignResult) return;
    if (!window.confirm(`¿Aplicar ${autoAssignResult.assignments?.length || 0} asignaciones? Se sobrescribirán las auto-asignaciones previas (las manuales se respetan).`)) return;
    setAutoAssignApplying(true);
    try {
      const { data } = await api.post(`/admin/technicians/auto-assign-warehouses?dry_run=false&min_score=${minScore}`);
      toast.success(`✅ ${data.applied} técnicos auto-asignados (${data.exact_matches} exactos · ${data.probable_matches} probables · ${data.ambiguous} ambiguos)`);
      setShowAutoAssign(false);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error aplicando asignación");
    } finally {
      setAutoAssignApplying(false);
    }
  };
  // v2.7.1 · Escape cierra el modal auto-assign
  useEffect(() => {
    if (!showAutoAssign) return;
    const onKey = (e) => { if (e.key === "Escape" && !autoAssignApplying) setShowAutoAssign(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAutoAssign, autoAssignApplying]);

  return (
    <Layout>
      <ImpersonateModal user={impTarget} onClose={() => setImpTarget(null)} />
      <div className="space-y-6" data-testid="admin-users-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2" style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>Administración</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>Usuarios y Roles</h1>
            <p className="mt-1 text-sm" style={{ color: "#64748B" }}>Gestión de cuentas, asignación de roles y zonas operativas.</p>
          </div>
          <div className="flex items-center gap-2">
            {/* v3.9.3 · Descarga plantilla oficial Excel */}
            <button
              onClick={async () => {
                try {
                  const resp = await api.get("/v1/users/bulk-xlsx/template.xlsx", { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([resp.data]));
                  const a = document.createElement("a");
                  a.href = url; a.download = "usuarios_alfanet_template.xlsx"; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Plantilla descargada");
                } catch (e) {
                  toast.error("Error descargando plantilla");
                }
              }}
              disabled={!hasPerm("M17.users.create")}
              className="neu-btn"
              data-testid="download-template-btn"
              title="Descargar plantilla oficial .xlsx"
            >
              <Download size={14}/> Plantilla Excel
            </button>
            {/* v3.9.3 · Importar Excel con validaciones + summary */}
            <label
              className={`neu-btn cursor-pointer ${!hasPerm("M17.users.create") ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="bulk-import-xlsx-btn"
              title="Importar desde Excel (valida emails, cédulas y sucursales)"
            >
              <Upload size={14} /> Importar desde Excel
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                disabled={!hasPerm("M17.users.create")}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  const toastId = toast.loading("Procesando archivo Excel...");
                  try {
                    const { data } = await api.post("/v1/users/bulk-xlsx/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.dismiss(toastId);
                    setImportSummary(data);
                    if (data.created > 0) {
                      toast.success(`✅ ${data.created} usuarios creados${data.failed ? ` · ${data.failed} fallidos` : ""}`);
                      fetchAll();
                    } else {
                      toast.error(`Ningún usuario creado · ${data.failed} errores`);
                    }
                  } catch (err) {
                    toast.dismiss(toastId);
                    toast.error(err?.response?.data?.detail || "Error en importación");
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
            </label>
            {/* v101.205.0 · Excel «Contratistas y técnicos asignados» → enlaza cada técnico líder con su dueño */}
            <CuadrillasImportar onDone={fetchAll} disabled={!hasPerm("M17.users.create")} />
            <button
              onClick={(e) => openCreateModal(e.currentTarget)}
              disabled={!hasPerm("M17.users.create")}
              className="neu-btn neu-btn-primary"
              data-testid="create-user-btn"
              title={hasPerm("M17.users.create") ? "Crear usuario" : "Requiere permiso M17.users.create"}
            >
              <Plus size={14} /> Nuevo usuario
            </button>
            {/* v2.7.1 · Auto-asignar bodegas a técnicos por nombre */}
            <button
              onClick={openAutoAssign}
              disabled={!hasPerm("M17.users.create")}
              className="neu-btn"
              data-testid="auto-assign-warehouses-btn"
              title="Auto-asignar Bodegas a Técnicos por coincidencia de nombre"
              style={{ background: "linear-gradient(135deg,#E0E621 0%,#C5CD00 100%)", color: "#0A3747", fontWeight: 600 }}
            >
              <Sparkles size={14} /> Auto-asignar Bodegas
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total" value={users.length} tone="navy" />
          <Stat label="Activos" value={users.filter((u) => u.active !== false).length} tone="green" />
          <Stat label="MFA activo" value={users.filter((u) => u.mfa_enabled).length} tone="cyan" />
          <Stat label="Bloqueados" value={users.filter((u) => u.locked_until && new Date(u.locked_until) > new Date()).length} tone="amber" />
        </div>

        {/* Search + filter — v6.3.3 · Filtros avanzados multi-eje */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="neu-input pl-11"
              data-testid="users-search"
            />
          </div>
          <div className="relative min-w-[180px]">
            <Filter size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="neu-input pl-10"
              data-testid="users-role-filter"
            >
              <option value="">Todos los roles</option>
              {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
            </select>
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="neu-input"
              data-testid="users-status-filter"
            >
              <option value="">Activos e inactivos</option>
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
            </select>
          </div>
          <div className="relative min-w-[180px]">
            <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="neu-input pl-10"
              data-testid="users-branch-filter"
            >
              <option value="">Todas las sucursales</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name || b.code}</option>)}
            </select>
          </div>
          <div className="relative min-w-[180px]">
            <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <select
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              className="neu-input pl-10"
              data-testid="users-zone-filter"
            >
              <option value="">Todas las zonas</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name || z.code}</option>)}
            </select>
          </div>
          {(q || roleFilter || branchFilter || zoneFilter || statusFilter) && (
            <button
              onClick={() => { setQ(""); setRoleFilter(""); setBranchFilter(""); setZoneFilter(""); setStatusFilter(""); }}
              className="neu-btn"
              style={{ padding: "8px 14px", fontSize: 13 }}
              data-testid="clear-filters-btn"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {/* v6.3.3 · Barra de Acciones Masivas Flotante */}
        {selected.size > 0 && (
          <div
            className="sticky top-2 z-30 mx-auto flex items-center gap-3 px-5 py-3 animate-in fade-in slide-in-from-top-2"
            style={{
              background: "#0A3747",
              borderRadius: 22,
              maxWidth: 720,
              boxShadow: "0 8px 32px rgba(10,55,71,0.35), 0 2px 8px rgba(0,0,0,0.2)",
            }}
            data-testid="bulk-action-bar"
          >
            <span className="text-white/90 text-xs font-mono flex-shrink-0">
              <span className="font-black text-white">{selected.size}</span> seleccionado{selected.size !== 1 ? "s" : ""}
            </span>
            <div className="flex-1" />
            <button
              onClick={() => setBulkAction("reset")}
              className="px-3 py-1.5 rounded-xl text-xs font-bold text-amber-100 transition hover:bg-amber-500/20"
              style={{ background: "rgba(245,158,11,0.15)" }}
              data-testid="bulk-reset-btn"
            >
              <KeyRound size={13} className="inline mr-1 -mt-0.5" />
              Reset Passwords
            </button>
            <button
              onClick={() => setBulkAction("role")}
              className="px-3 py-1.5 rounded-xl text-xs font-bold text-blue-100 transition hover:bg-blue-500/20"
              style={{ background: "rgba(59,130,246,0.15)" }}
              data-testid="bulk-role-btn"
            >
              <UserCog size={13} className="inline mr-1 -mt-0.5" />
              Cambiar Rol
            </button>
            <button
              onClick={() => setBulkAction("delete")}
              className="px-3 py-1.5 rounded-xl text-xs font-bold text-red-200 transition hover:bg-red-500/20"
              style={{ background: "rgba(239,68,68,0.15)" }}
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={13} className="inline mr-1 -mt-0.5" />
              Eliminar
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-1 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
              data-testid="bulk-clear-btn"
              aria-label="Deseleccionar"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="neu-card overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm neu-table">
                <thead>
                  <tr>
                    <th className="px-3 py-3 w-10">
                      {(() => {
                        const pageIds = pg.paged.map((u) => u.id);
                        const allSel = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
                        const someSel = pageIds.some((id) => selected.has(id));
                        return (
                          <input
                            type="checkbox"
                            checked={allSel}
                            ref={(el) => { if (el) el.indeterminate = !allSel && someSel; }}
                            onChange={() => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (allSel) pageIds.forEach((id) => next.delete(id));
                                else pageIds.forEach((id) => next.add(id));
                                return next;
                              });
                            }}
                            className="w-4 h-4 cursor-pointer accent-[#0A3747]"
                            data-testid="select-all-users"
                            aria-label="Seleccionar todos"
                          />
                        );
                      })()}
                    </th>
                    <th className="px-4 py-3 font-medium">Usuario</th>
                    <th className="px-4 py-3 font-medium">Rol</th>
                    <th className="px-4 py-3 font-medium">Bodega</th>
                    <th className="px-4 py-3 font-medium">MFA</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="px-4 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pg.paged.map((u) => {
                    const locked = u.locked_until && new Date(u.locked_until) > new Date();
                    return (
                      <tr key={u.id} className={`border-t border-gray-200 hover:bg-gray-50 ${selected.has(u.id) ? "bg-blue-50/50" : ""}`} data-testid={`user-row-${u.id}`}>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(u.id)}
                            onChange={() => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(u.id)) next.delete(u.id);
                                else next.add(u.id);
                                return next;
                              });
                            }}
                            className="w-4 h-4 cursor-pointer accent-[#0A3747]"
                            data-testid={`select-user-${u.id}`}
                            aria-label={`Seleccionar ${u.name}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-gray-500 text-xs flex items-center gap-1"><Mail size={11} /> {u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-800"}`}>
                            {u.role_display || u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`user-warehouse-${u.id}`}>
                          {u.warehouse_location_id || u.return_warehouse_location_id ? (
                            <div className="flex flex-col gap-0.5">
                              {u.warehouse_location_id ? (
                                <div className="inline-flex items-center gap-1.5">
                                  {u.wh_auto_assigned ? (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                      style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }}
                                      title={`Auto-asignado (score ${u.wh_auto_score ?? "?"})${u.wh_auto_assigned_at ? " · " + new Date(u.wh_auto_assigned_at).toLocaleString() : ""}`}
                                      data-testid={`wh-auto-badge-${u.id}`}
                                    >
                                      <CheckCircle2 size={12} /> Auto
                                    </span>
                                  ) : (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                                      style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #CBD5E1" }}
                                      title="Asignado manualmente"
                                      data-testid={`wh-manual-badge-${u.id}`}
                                    >
                                      <UserCog size={11} /> Manual
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-600 font-mono" title="Bodega de materiales">Mat #{u.warehouse_location_id}</span>
                                </div>
                              ) : null}
                              {/* v101.6.0 · Bodega de devoluciones */}
                              {u.return_warehouse_location_id ? (
                                <span className="text-[10px] text-purple-700 font-mono" title="Bodega de devoluciones" data-testid={`wh-return-${u.id}`}>
                                  Dev #{u.return_warehouse_location_id}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400 italic">Sin bodega</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {u.mfa_enabled
                            ? <span className="text-green-700 inline-flex items-center gap-1 text-xs"><ShieldCheck size={13}/> Activo</span>
                            : <span className="text-gray-400 inline-flex items-center gap-1 text-xs"><ShieldOff size={13}/> Inactivo</span>}
                        </td>
                        <td className="px-4 py-3">
                          {locked ? (
                            <span className="text-red-700 text-xs font-medium">🔒 Bloqueado</span>
                          ) : u.active === false ? (
                            <span className="text-gray-500 text-xs">Inactivo</span>
                          ) : (
                            <span className="text-green-700 text-xs">● Activo</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          {locked && hasPerm("M17.users.unlock") && (
                            <button onClick={() => unlockUser(u)} className="text-xs px-2 py-1 border border-orange-300 text-orange-700 rounded hover:bg-orange-50" data-testid={`unlock-${u.id}`} title="Desbloquear cuenta">
                              <Unlock size={12} className="inline" />
                            </button>
                          )}
                          {currentUser?.role === "super_admin" && !isImpersonating() && u.role !== "super_admin" && u.active !== false && (
                            <button
                              onClick={() => setImpTarget(u)}
                              className="text-xs p-1.5 border border-[#0A3747]/40 text-[#0A3747] rounded hover:bg-[#E0E621]/30"
                              title="Entrar como este usuario (solo Super Administrador · auditado)"
                              data-testid={`impersonate-${u.id}`}
                            >
                              <LogIn size={13} />
                            </button>
                          )}
                          {hasPerm("M17.users.edit") && (
                            <button
                              onClick={(e) => openEdit(u, e.currentTarget)}
                              className="text-xs p-1.5 border border-gray-300 rounded hover:bg-gray-50 hover:border-[#E0E621]"
                              title="Editar usuario"
                              data-testid={`edit-${u.id}`}
                            >
                              <Pencil size={13} />
                            </button>
                          )}
                          {(hasPerm("M17.users.edit") || hasPerm("M17.users.reset_password")) && (
                            <button
                              onClick={() => resetPassword(u)}
                              className="text-xs p-1.5 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 hover:border-blue-500"
                              title="Resetear contraseña (genera clave temporal)"
                              data-testid={`reset-pwd-${u.id}`}
                            >
                              <KeyRound size={13} />
                            </button>
                          )}
                          {hasPerm("M17.users.edit") && (
                            <button
                              onClick={() => toggleActive(u)}
                              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                              data-testid={`toggle-active-${u.id}`}
                              title={u.active === false ? "Activar usuario" : "Desactivar usuario"}
                            >
                              {u.active === false ? "Activar" : "Desactivar"}
                            </button>
                          )}
                          {hasPerm("M17.users.delete") && u.role !== "super_admin" && (
                            <button
                              onClick={() => openDeleteConfirm(u)}
                              className="text-xs p-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50 hover:border-red-500 transition"
                              title="Eliminar permanentemente"
                              data-testid={`delete-${u.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Sin usuarios para mostrar.</td></tr>
                  )}
                </tbody>
              </table>
              {pg.total > 0 && (
                <Pagination
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.setPage}
                  onPageSizeChange={pg.setPageSize}
                  testIdPrefix="users-pagination"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create — pantalla completa (fallback "Ver detalle completo") */}
      {showCreate && showCreateFull && (
        <PageForm
          title="Nuevo usuario"
          breadcrumb="Administración › Usuarios › Nuevo"
          subtitle="Completa los datos para dar de alta un usuario"
          onBack={closeCreateModal}
          maxWidth="max-w-2xl"
          testId="create-user-page"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <form onSubmit={createUser} className="p-6 space-y-4" autoComplete="off">
              {/* Trampa anti-autocomplete de Chrome */}
              <input type="text" name="prevent_autofill" autoComplete="off" style={{display:"none"}} />
              <input type="password" name="prevent_autofill_pw" autoComplete="off" style={{display:"none"}} />
              <Field label="Nombre completo">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false}
                  className="w-full input" data-testid="new-user-name" />
              </Field>
              <Field label="Email corporativo">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoComplete="new-email" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                  className="w-full input" data-testid="new-user-email" />
              </Field>
              <Field label="Teléfono (opcional)">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  autoComplete="off" className="w-full input" />
              </Field>
              <Field label="Rol">
                <select required value={form.role} onChange={(e) => handleCreateRoleChange(e.target.value)} className="w-full input" data-testid="new-user-role">
                  {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
                </select>
              </Field>
              {FIELD_ROLES.includes(form.role) && (
                <Field label="Asignación jerárquica (Sucursal → Bodega → Zonas)">
                  <BranchHierarchyPicker
                    branch_id={form.branch_id}
                    warehouse_location_id={form.warehouse_location_id}
                    return_warehouse_location_id={form.return_warehouse_location_id}
                    zone_ids={form.zone_ids}
                    onChange={(vals) => setForm({ ...form, ...vals })}
                    branches={branches}
                    zones={zones}
                    testIdPrefix="new-user"
                    zonesReadOnly
                  />
                </Field>
              )}
              <Field label="Contraseña inicial">
                <div className="relative">
                  <input required type={showPassword ? "text" : "password"} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    className="w-full input pr-10" data-testid="new-user-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0A3747] p-1"
                    title={showPassword ? "Ocultar" : "Ver contraseña"} data-testid="toggle-password">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Mínimo 12 caracteres, mayúsculas, minúsculas, números y símbolos.</p>
              </Field>
              <div className="flex gap-2 pt-3 border-t">
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2" data-testid="submit-new-user">
                  {creating ? <Loader2 className="animate-spin" size={14} /> : <UserCog size={14} />}
                  Crear usuario
                </button>
                <button type="button" onClick={closeCreateModal} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">Cancelar</button>
              </div>
            </form>
          </div>
        </PageForm>
      )}

      {/* Create — Wizard popover (default: emerges from "Nuevo usuario" button) */}
      {showCreate && !showCreateFull && (
        <WizardPopover
          open={true}
          onOpenChange={(o) => !o && closeCreateModal()}
          anchorRef={createAnchorRef}
          title="Nuevo usuario"
          subtitle="Alta rápida"
          icon={UserCog}
          saving={creating}
          saveLabel="Crear"
          onSave={createUser}
          onOpenFull={() => setShowCreateFull(true)}
          testid="create-user-wizard"
          width={500}
          steps={[
            {
              key: "basic",
              title: "Datos",
              icon: User,
              content: (
                <div className="space-y-3" autoComplete="off">
                  <input type="text" name="prevent_autofill" autoComplete="off" style={{display:"none"}} />
                  <input type="password" name="prevent_autofill_pw" autoComplete="off" style={{display:"none"}} />
                  <div>
                    <label>Nombre completo</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="off" spellCheck={false} data-testid="new-user-name" />
                  </div>
                  <div>
                    <label>Email corporativo</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="new-email" spellCheck={false} data-testid="new-user-email" />
                  </div>
                  <div>
                    <label>Teléfono (opcional)</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="off" placeholder="+593 …" />
                  </div>
                </div>
              ),
            },
            {
              key: "access",
              title: "Acceso",
              icon: Shield,
              content: (
                <div className="space-y-3">
                  <div>
                    <label>Rol</label>
                    <select required value={form.role} onChange={(e) => handleCreateRoleChange(e.target.value)} data-testid="new-user-role">
                      {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
                    </select>
                  </div>
                  {FIELD_ROLES.includes(form.role) && (
                    <div>
                      <label>Asignación jerárquica (Sucursal → Bodega → Zonas)</label>
                      <BranchHierarchyPicker
                        branch_id={form.branch_id}
                        warehouse_location_id={form.warehouse_location_id}
                        return_warehouse_location_id={form.return_warehouse_location_id}
                        zone_ids={form.zone_ids}
                        onChange={(vals) => setForm({ ...form, ...vals })}
                        branches={branches}
                        zones={zones}
                        testIdPrefix="new-user-wiz"
                        zonesReadOnly
                      />
                    </div>
                  )}
                  <div>
                    <label>Contraseña inicial</label>
                    <div className="relative">
                      <input required type={showPassword ? "text" : "password"} value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        autoComplete="new-password" spellCheck={false}
                        data-testid="new-user-password" style={{ paddingRight: 34 }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0A3747] p-1"
                        title={showPassword ? "Ocultar" : "Ver contraseña"} data-testid="toggle-password">
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
                      Mín. 12 caracteres, mayús, minús, números y símbolos.
                    </p>
                  </div>
                </div>
              ),
            },
          ]}
        />
      )}
      {editingUser && editingFull && (
        <PageForm
          title={`Editar usuario · ${editingUser.name}`}
          breadcrumb="Administración › Usuarios › Editar"
          subtitle={editingUser.email}
          onBack={closeEdit}
          maxWidth="max-w-2xl"
          testId="edit-user-page"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <div className="text-xs text-gray-500 mb-2">
                <Mail size={12} className="inline mr-1" />{editingUser.email}
              </div>
              <Field label="Nombre completo">
                <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full input" data-testid="edit-user-name" />
              </Field>
              <Field label="Rol">
                <select required value={editForm.role} onChange={(e) => handleEditRoleChange(e.target.value)} className="w-full input" data-testid="edit-user-role">
                  {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
                </select>
              </Field>
              {FIELD_ROLES.includes(editForm.role) && (
                <Field label="Asignación jerárquica (Sucursal → Bodega → Zonas)">
                  <BranchHierarchyPicker
                    branch_id={editForm.branch_id}
                    warehouse_location_id={editForm.warehouse_location_id}
                    return_warehouse_location_id={editForm.return_warehouse_location_id}
                    zone_ids={editForm.zone_ids}
                    onChange={(vals) => setEditForm({ ...editForm, ...vals })}
                    branches={branches}
                    zones={zones}
                    testIdPrefix="edit-user"
                    zonesReadOnly
                  />
                </Field>
              )}
              {FIELD_ROLES.includes(editForm.role) && (
                <Field label="Jornada (turno)">
                  <JornadaPicker userId={editingUser?.id} zoneIds={editForm.zone_ids}
                                 warehouseId={editForm.warehouse_location_id} testIdPrefix="edit-user-jornada" />
                </Field>
              )}
              {FIELD_ROLES.includes(editForm.role) && (
                <Field label="Contratista / dueño y cuadrilla">
                  <CuadrillaContratista userId={editingUser?.id} testIdPrefix="edit-user-cuadrilla" />
                </Field>
              )}
              <Field label="Teléfono">
                <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full input" />
              </Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm">Cuenta activa</span>
              </label>
              <div className="flex gap-2 pt-3 border-t">
                <button type="submit" disabled={savingEdit} className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2" data-testid="save-edit-user">
                  {savingEdit ? <Loader2 className="animate-spin" size={14} /> : <Pencil size={14} />}
                  Guardar cambios
                </button>
                <button type="button" onClick={closeEdit} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">Cancelar</button>
              </div>
            </form>
          </div>
        </PageForm>
      )}

      {/* Edit — Wizard popover (default: emerges from clicked pencil) */}
      {editingUser && !editingFull && (
        <WizardPopover
          open={true}
          onOpenChange={(o) => !o && closeEdit()}
          anchorRef={editAnchorRef}
          title={`Editar · ${editingUser.name}`}
          subtitle={editingUser.email}
          icon={UserCog}
          saving={savingEdit}
          saveLabel="Guardar"
          onSave={saveEdit}
          onOpenFull={() => setEditingFull(true)}
          testid="edit-user-wizard"
          steps={[
            {
              key: "identity",
              title: "Identidad",
              icon: User,
              content: (
                <div className="space-y-3">
                  <div>
                    <label>Nombre completo</label>
                    <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
                  </div>
                  <div>
                    <label>Teléfono</label>
                    <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+593 …" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-[13px]" style={{ textTransform: "none", fontFamily: "inherit", fontWeight: 500, letterSpacing: 0, marginBottom: 0 }}>
                    <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
                    <span>Cuenta activa</span>
                  </label>
                </div>
              ),
            },
            {
              key: "role",
              title: "Rol & Zonas",
              icon: Shield,
              content: (
                <div className="space-y-3">
                  <div>
                    <label>Rol</label>
                    <select required value={editForm.role} onChange={(e) => handleEditRoleChange(e.target.value)} data-testid="edit-user-role">
                      {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
                    </select>
                  </div>
                  {FIELD_ROLES.includes(editForm.role) && (
                    <div>
                      <label>Asignación jerárquica (Sucursal → Bodega → Zonas)</label>
                      <BranchHierarchyPicker
                        branch_id={editForm.branch_id}
                        warehouse_location_id={editForm.warehouse_location_id}
                        return_warehouse_location_id={editForm.return_warehouse_location_id}
                        zone_ids={editForm.zone_ids}
                        onChange={(vals) => setEditForm({ ...editForm, ...vals })}
                        branches={branches}
                        zones={zones}
                        testIdPrefix="edit-user-wiz"
                        zonesReadOnly
                      />
                    </div>
                  )}
                  {FIELD_ROLES.includes(editForm.role) && (
                    <div>
                      <label>Jornada (turno)</label>
                      <JornadaPicker userId={editingUser?.id} zoneIds={editForm.zone_ids}
                                     warehouseId={editForm.warehouse_location_id} testIdPrefix="edit-user-wiz-jornada" />
                    </div>
                  )}
                  <TechnicianAssignment userId={editingUser?.id} userName={editForm.name} role={editForm.role} />
                </div>
              ),
            },
            ...(FIELD_ROLES.includes(editForm.role) ? [{
              key: "contratista",
              title: "Contratista",
              icon: Users,
              content: <CuadrillaContratista userId={editingUser?.id} testIdPrefix="edit-user-wiz-cuadrilla" />,
            }] : []),   // v101.205.0
          ]}
        />
      )}

      <style>{`
        .input { padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.875rem; }
        .input:focus { outline: none; border-color: #E0E621; box-shadow: 0 0 0 2px rgba(200,230,0,0.2); }
      `}</style>

      {/* Delete confirmation modal */}
      {deletingUser && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeDeleteConfirm}
          data-testid="delete-user-modal-backdrop"
        >
          <div
            className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-user-modal"
          >
            <div className="bg-red-50 border-b border-red-100 px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-red-900">Eliminar usuario permanentemente</h2>
                <p className="text-xs text-red-700 mt-0.5">Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">
                Estás a punto de eliminar permanentemente al siguiente usuario del sistema
                (tanto de MongoDB como de PostgreSQL):
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="font-medium text-gray-900">{deletingUser.name}</div>
                <div className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                  <Mail size={11} /> {deletingUser.email}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Rol: <span className="font-medium">{deletingUser.role_display || deletingUser.role}</span>
                </div>
              </div>
              <div className="bg-amber-50 border-l-2 border-amber-400 px-3 py-2 text-xs text-amber-800">
                <strong>Nota:</strong> Los registros históricos asociados (órdenes, evidencias,
                movimientos, auditoría) se preservarán con referencia como &quot;Usuario eliminado&quot;.
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={confirmingDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-user"
                >
                  {confirmingDelete ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                  Sí, eliminar permanentemente
                </button>
                <button
                  type="button"
                  onClick={closeDeleteConfirm}
                  disabled={confirmingDelete}
                  className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
                  data-testid="cancel-delete-user"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v3.9.3 · Modal resumen de importación Excel */}
      {importSummary && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setImportSummary(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-summary-modal">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ color: "#0A3747" }}>Resumen de importación</h3>
              <button onClick={() => setImportSummary(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg text-center border-2 border-blue-200 bg-blue-50">
                  <div className="text-2xl font-bold text-blue-800 tabular-nums">{importSummary.total}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-blue-600 mt-0.5">Total filas</div>
                </div>
                <div className="p-3 rounded-lg text-center border-2 border-emerald-200 bg-emerald-50">
                  <div className="text-2xl font-bold text-emerald-800 tabular-nums" data-testid="summary-created">{importSummary.created}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 mt-0.5">✓ Creados</div>
                </div>
                <div className="p-3 rounded-lg text-center border-2 border-red-200 bg-red-50">
                  <div className="text-2xl font-bold text-red-800 tabular-nums" data-testid="summary-failed">{importSummary.failed}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-red-600 mt-0.5">✗ Fallidos</div>
                </div>
              </div>
              {/* Tabla errores */}
              {importSummary.errors && importSummary.errors.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Errores detallados</div>
                  <div className="border border-gray-200 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-bold text-gray-600">Fila</th>
                          <th className="px-2 py-1.5 text-left font-bold text-gray-600">Email</th>
                          <th className="px-2 py-1.5 text-left font-bold text-gray-600">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importSummary.errors.map((e, i) => (
                          <tr key={i} className="border-t border-gray-100" data-testid={`err-${i}`}>
                            <td className="px-2 py-1.5 font-mono text-gray-500">{e.row}</td>
                            <td className="px-2 py-1.5 font-mono truncate max-w-[180px]">{e.email || "—"}</td>
                            <td className="px-2 py-1.5 text-red-700">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {importSummary.failed === 0 && (
                <p className="text-sm text-emerald-700 text-center py-2">🎉 Todos los usuarios fueron creados exitosamente.</p>
              )}
            </div>
            <div className="p-3 border-t border-gray-200 flex justify-end">
              <button onClick={() => setImportSummary(null)} className="neu-btn neu-btn-primary" data-testid="close-summary">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* v2.7.1 · Modal Auto-asignación de Bodegas a Técnicos */}
      {showAutoAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,55,71,0.55)", backdropFilter: "blur(4px)" }} data-testid="auto-assign-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between" style={{ background: "linear-gradient(135deg,#0A3747,#0E4557)" }}>
              <div className="flex items-center gap-2 text-white">
                <Sparkles size={18} style={{ color: "#E0E621" }} />
                <div>
                  <div className="text-sm font-semibold">Auto-asignar Bodegas a Técnicos</div>
                  <div className="text-[11px] opacity-70">Compara tokens del nombre completo contra <span className="font-mono">master_inventory.location_name</span></div>
                </div>
              </div>
              <button onClick={() => setShowAutoAssign(false)} className="text-white hover:text-yellow-300 p-1" data-testid="close-auto-assign">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {!autoAssignResult && !autoAssignRunning && (
                <div className="text-center py-10 space-y-4">
                  <div className="text-5xl">🎯</div>
                  <h3 className="text-lg font-bold" style={{ color: "#0A3747" }}>Previsualización de asignaciones</h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    Ejecuta primero una <b>previsualización (Dry-Run)</b> para ver qué técnicos van a auto-asignarse.
                    Después podrás aplicarla. Ajusta el <b>umbral mínimo</b> de coincidencia si no ves resultados.
                  </p>
                  <div className="max-w-xs mx-auto space-y-2">
                    <label className="block text-xs font-semibold text-gray-700">
                      Umbral mínimo (score): <span className="font-mono text-yellow-700" data-testid="min-score-value">{minScore.toFixed(2)}</span>
                    </label>
                    <input
                      type="range"
                      min="0.4"
                      max="1.0"
                      step="0.05"
                      value={minScore}
                      onChange={(e) => setMinScore(parseFloat(e.target.value))}
                      className="w-full"
                      data-testid="min-score-slider"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500">
                      <span>0.40 · más permisivo</span>
                      <span>1.00 · exacto</span>
                    </div>
                  </div>
                  <button
                    onClick={runAutoAssignDryRun}
                    className="neu-btn neu-btn-primary"
                    data-testid="run-dry-run-btn"
                    style={{ padding: "10px 20px" }}
                  >
                    <Sparkles size={14} /> Ejecutar Previsualización
                  </button>
                </div>
              )}

              {autoAssignRunning && (
                <div className="text-center py-16">
                  <Loader2 className="animate-spin mx-auto text-gray-400" size={32} />
                  <p className="text-sm text-gray-500 mt-3">Analizando técnicos y bodegas…</p>
                </div>
              )}

              {autoAssignResult && (
                <>
                  {/* Control umbral + re-run rápido */}
                  <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}>
                    <span className="text-[11px] font-semibold text-yellow-900">Umbral:</span>
                    <input
                      type="range"
                      min="0.4"
                      max="1.0"
                      step="0.05"
                      value={minScore}
                      onChange={(e) => setMinScore(parseFloat(e.target.value))}
                      className="flex-1 max-w-xs"
                      data-testid="min-score-slider-inline"
                    />
                    <span className="font-mono text-xs text-yellow-900 font-bold">{minScore.toFixed(2)}</span>
                    <button
                      onClick={runAutoAssignDryRun}
                      disabled={autoAssignRunning}
                      className="neu-btn text-[11px] py-1"
                      data-testid="reapply-threshold"
                    >
                      {autoAssignRunning ? <Loader2 size={11} className="animate-spin" /> : "↻"} Recalcular
                    </button>
                  </div>

                  {/* Stats grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <StatChip label="Técnicos" value={autoAssignResult.techs_total} color="#0A3747" />
                    <StatChip label="Match exacto" value={autoAssignResult.exact_matches} color="#059669" />
                    <StatChip label="Probable" value={autoAssignResult.probable_matches} color="#0891B2" />
                    <StatChip label="Ambiguos" value={autoAssignResult.ambiguous} color="#D97706" />
                    <StatChip label="Sin match" value={autoAssignResult.no_match} color="#6B7280" />
                    <StatChip label="Manual (skip)" value={autoAssignResult.skipped_manual} color="#7C3AED" />
                    <StatChip label="A aplicar" value={autoAssignResult.assignments?.length || 0} color="#0A3747" bold />
                    <StatChip label={autoAssignResult.dry_run ? "Modo" : "Aplicados"} value={autoAssignResult.dry_run ? "DRY-RUN" : autoAssignResult.applied} color={autoAssignResult.dry_run ? "#D97706" : "#059669"} />
                  </div>

                  {/* Tabla de asignaciones */}
                  {(autoAssignResult.assignments?.length || 0) > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[52vh] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600">Técnico</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600">Bodega sugerida</th>
                              <th className="px-3 py-2 text-center font-semibold text-gray-600">Sucursal</th>
                              <th className="px-3 py-2 text-center font-semibold text-gray-600">Score</th>
                              <th className="px-3 py-2 text-center font-semibold text-gray-600">Flag</th>
                            </tr>
                          </thead>
                          <tbody>
                            {autoAssignResult.assignments.map((a, i) => (
                              <tr key={a.tech_id} className="border-t border-gray-100 hover:bg-yellow-50/50" data-testid={`assignment-row-${i}`}>
                                <td className="px-3 py-1.5">
                                  <div className="font-medium">{a.tech_name}</div>
                                  <div className="text-[10px] text-gray-500 font-mono">{a.tech_email}</div>
                                </td>
                                <td className="px-3 py-1.5">
                                  <div className="font-mono text-[11px]">#{a.location_id}</div>
                                  <div className="text-[10px] text-gray-600">{a.location_name}</div>
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                  {a.branch_nomenclatura ? (
                                    <span className="inline-block px-1.5 py-0.5 rounded font-mono text-[10px] font-bold" style={{ background: "#E0E621", color: "#0A3747" }}>
                                      {a.branch_nomenclatura}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-center font-mono font-semibold" style={{ color: a.score >= 1.0 ? "#059669" : a.score >= 0.85 ? "#0891B2" : "#D97706" }}>
                                  {a.score}
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                  {a.ambiguous ? (
                                    <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#FED7AA", color: "#9A3412" }}>
                                      Ambiguo
                                    </span>
                                  ) : a.score >= 1.0 ? (
                                    <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#DCFCE7", color: "#166534" }}>
                                      Exacto
                                    </span>
                                  ) : (
                                    <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#E0F2FE", color: "#075985" }}>
                                      Probable
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-sm text-gray-500 italic">
                      Ningún técnico coincide con score ≥ 0.75. Revisa que los nombres de las bodegas contengan los nombres de los técnicos.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
              <div className="text-[11px] text-gray-500">
                {autoAssignResult ? (
                  <>Umbral: <span className="font-mono">≥ {minScore.toFixed(2)}</span> · Las asignaciones manuales previas NO se sobrescriben.</>
                ) : (
                  <>Cambios reversibles editando manualmente el picker de Sucursal/Bodega.</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowAutoAssign(false)} className="neu-btn" data-testid="cancel-auto-assign">Cancelar</button>
                {autoAssignResult && (
                  <>
                    <button onClick={runAutoAssignDryRun} disabled={autoAssignRunning} className="neu-btn" data-testid="rerun-dry-run">
                      {autoAssignRunning ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Re-previsualizar
                    </button>
                    <button
                      onClick={applyAutoAssign}
                      disabled={autoAssignApplying || (autoAssignResult.assignments?.length || 0) === 0}
                      className="neu-btn neu-btn-primary"
                      data-testid="apply-auto-assign"
                      style={{ background: "#059669", color: "white" }}
                    >
                      {autoAssignApplying ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      Aplicar {autoAssignResult.assignments?.length || 0} asignaciones
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v2.9.0 · Modal · Reset admin de contraseña */}
      {resetTarget && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeResetModal(); }}
          data-testid="admin-reset-pwd-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <KeyRound size={20} className="text-blue-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {resetResult ? "Contraseña reseteada" : "Resetear contraseña"}
                </h3>
                <p className="text-xs text-gray-500 truncate">{resetTarget.email}</p>
              </div>
            </div>

            {!resetResult ? (
              <>
                <div className="text-sm text-gray-700 leading-relaxed">
                  Se generará una <b>clave temporal aleatoria</b> para{" "}
                  <span className="font-mono text-blue-700">{resetTarget.name || resetTarget.email}</span>.
                  El usuario deberá cambiarla en su próximo inicio de sesión.
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  ⚠ Las sesiones activas del usuario serán invalidadas.
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    onClick={closeResetModal}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="reset-pwd-cancel"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmResetPassword}
                    disabled={resetLoading}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="reset-pwd-confirm"
                  >
                    {resetLoading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                    Generar clave temporal
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-700">
                  Comparte esta clave con el usuario por un <b>canal seguro</b>. Solo se muestra una vez.
                </div>
                <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-3 space-y-2">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 font-semibold">
                    Clave temporal
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type={showTempPwd ? "text" : "password"}
                      readOnly
                      value={resetResult.temporary_password}
                      className="flex-1 font-mono text-sm px-2 py-1.5 border border-emerald-400 rounded bg-white"
                      data-testid="reset-pwd-temp-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTempPwd((v) => !v)}
                      className="p-1.5 border border-emerald-400 rounded hover:bg-emerald-100"
                      title={showTempPwd ? "Ocultar" : "Mostrar"}
                      data-testid="reset-pwd-toggle-visibility"
                    >
                      {showTempPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={copyTempPassword}
                      className="p-1.5 border border-emerald-500 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                      title="Copiar al portapapeles"
                      data-testid="reset-pwd-copy"
                    >
                      <Download size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end pt-2">
                  <button
                    onClick={closeResetModal}
                    className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                    data-testid="reset-pwd-close"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════ v6.3.3 · Modales de Acciones Masivas ═══════ */}

      {/* Modal: Bulk Reset Passwords */}
      {bulkAction === "reset" && !bulkResetResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-reset-modal">
          <div className="neu-card p-6 max-w-md w-full mx-4 space-y-4" style={{ borderRadius: 22 }}>
            <h3 className="text-lg font-extrabold text-[#0A3747] flex items-center gap-2">
              <KeyRound size={20} className="text-amber-500" /> Reset Masivo de Contraseñas
            </h3>
            <p className="text-sm text-gray-600">
              Se generarán contraseñas temporales para <span className="font-bold text-[#0A3747]">{selected.size}</span> usuario{selected.size !== 1 ? "s" : ""}. Cada uno deberá cambiar su clave en el próximo inicio de sesión.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkAction(null)} className="neu-btn px-4 py-2 text-sm" data-testid="bulk-reset-cancel">Cancelar</button>
              <button
                onClick={async () => {
                  setBulkRunning(true);
                  try {
                    const res = await api.post("/v1/users/bulk-reset-passwords", { user_ids: [...selected] });
                    const ok = (res.data.results || []).filter((r) => r.ok);
                    setBulkResetResults(ok);
                    toast.success(`${res.data.ok_count} contraseñas reseteadas`);
                    if (res.data.results.some((r) => !r.ok)) {
                      toast.error(`${res.data.results.filter((r) => !r.ok).length} fallaron`);
                    }
                  } catch (e) {
                    toast.error("Error al resetear contraseñas: " + (e.response?.data?.detail || e.message));
                  } finally {
                    setBulkRunning(false);
                  }
                }}
                disabled={bulkRunning}
                className="px-4 py-2 text-sm font-bold text-white rounded-xl transition"
                style={{ background: "#F59E0B" }}
                data-testid="bulk-reset-confirm"
              >
                {bulkRunning ? <Loader2 size={14} className="animate-spin inline mr-1" /> : <KeyRound size={14} className="inline mr-1" />}
                Resetear {selected.size} cuenta{selected.size !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Bulk Reset Results (lista de claves temporales) */}
      {bulkResetResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-reset-results-modal">
          <div className="neu-card p-6 max-w-lg w-full mx-4 space-y-4" style={{ borderRadius: 22 }}>
            <h3 className="text-lg font-extrabold text-[#0A3747] flex items-center gap-2">
              <CheckCircle2 size={20} className="text-emerald-500" /> Contraseñas Temporales
            </h3>
            <p className="text-xs text-gray-500">Copia estas claves ahora. No se mostrarán de nuevo.</p>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {bulkResetResults.map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl bg-gray-50 border text-xs font-mono" data-testid={`bulk-reset-result-${i}`}>
                  <span className="flex-1 truncate text-gray-600">{r.email}</span>
                  <span className="font-bold text-[#0A3747] select-all">{r.temporary_password}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(r.temporary_password); toast.success(`Clave copiada para ${r.email}`); }}
                    className="p-1 rounded hover:bg-gray-200 transition"
                    aria-label="Copiar"
                  >
                    <Download size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <button
                onClick={() => {
                  const csv = bulkResetResults.map((r) => `${r.email},${r.temporary_password}`).join("\n");
                  navigator.clipboard.writeText("email,password\n" + csv);
                  toast.success("Tabla copiada al portapapeles");
                }}
                className="neu-btn px-3 py-1.5 text-xs"
                data-testid="bulk-reset-copy-all"
              >
                <Download size={12} className="inline mr-1" /> Copiar todo
              </button>
              <button
                onClick={() => { setBulkResetResults(null); setBulkAction(null); setSelected(new Set()); fetchAll(); }}
                className="px-4 py-2 text-sm font-bold text-white rounded-xl"
                style={{ background: "#0A3747" }}
                data-testid="bulk-reset-done"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Bulk Change Role */}
      {bulkAction === "role" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-role-modal">
          <div className="neu-card p-6 max-w-md w-full mx-4 space-y-4" style={{ borderRadius: 22 }}>
            <h3 className="text-lg font-extrabold text-[#0A3747] flex items-center gap-2">
              <UserCog size={20} className="text-blue-500" /> Cambiar Rol Masivo
            </h3>
            <p className="text-sm text-gray-600">
              Se actualizará el rol de <span className="font-bold text-[#0A3747]">{selected.size}</span> usuario{selected.size !== 1 ? "s" : ""}.
            </p>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Nuevo Rol</label>
              <select
                value={bulkNewRole}
                onChange={(e) => setBulkNewRole(e.target.value)}
                className="neu-input mt-1 w-full"
                data-testid="bulk-role-select"
              >
                {roles.map((r) => <option key={r.code} value={r.code}>{r.display}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkAction(null)} className="neu-btn px-4 py-2 text-sm" data-testid="bulk-role-cancel">Cancelar</button>
              <button
                onClick={async () => {
                  // v99.5.0 · Advertencia si el nuevo rol no es de campo
                  if (!FIELD_ROLES.includes(bulkNewRole)) {
                    if (!window.confirm("El rol seleccionado no es de campo. Se limpiarán Bodega y Zonas de los usuarios afectados. ¿Continuar?")) return;
                  }
                  setBulkRunning(true);
                  try {
                    const res = await api.post("/v1/users/bulk-change-role", { user_ids: [...selected], new_role: bulkNewRole });
                    toast.success(`${res.data.ok_count} roles actualizados a ${bulkNewRole}`);
                    if (res.data.errors?.length) toast.error(`${res.data.errors.length} fallaron`);
                    setBulkAction(null);
                    setSelected(new Set());
                    fetchAll();
                  } catch (e) {
                    toast.error("Error: " + (e.response?.data?.detail || e.message));
                  } finally {
                    setBulkRunning(false);
                  }
                }}
                disabled={bulkRunning}
                className="px-4 py-2 text-sm font-bold text-white rounded-xl transition"
                style={{ background: "#3B82F6" }}
                data-testid="bulk-role-confirm"
              >
                {bulkRunning ? <Loader2 size={14} className="animate-spin inline mr-1" /> : <UserCog size={14} className="inline mr-1" />}
                Aplicar Rol
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Bulk Delete */}
      {bulkAction === "delete" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-delete-modal">
          <div className="neu-card p-6 max-w-md w-full mx-4 space-y-4" style={{ borderRadius: 22 }}>
            <h3 className="text-lg font-extrabold text-red-600 flex items-center gap-2">
              <AlertTriangle size={20} /> Eliminación Masiva
            </h3>
            <p className="text-sm text-gray-600">
              Estás a punto de eliminar <span className="font-bold text-red-600">{selected.size}</span> usuario{selected.size !== 1 ? "s" : ""} permanentemente. Esta acción <span className="font-bold">NO se puede deshacer</span>.
            </p>
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
              Los usuarios super_admin no serán eliminados por seguridad. Las OTs asignadas a estos usuarios quedarán sin asignar.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkAction(null)} className="neu-btn px-4 py-2 text-sm" data-testid="bulk-delete-cancel">Cancelar</button>
              <button
                onClick={async () => {
                  setBulkRunning(true);
                  try {
                    const res = await api.post("/v1/users/bulk-delete", { user_ids: [...selected] });
                    toast.success(`${res.data.ok_count} usuario${res.data.ok_count !== 1 ? "s" : ""} eliminado${res.data.ok_count !== 1 ? "s" : ""}`);
                    if (res.data.errors?.length) {
                      res.data.errors.forEach((e) => toast.error(`${e.user_id}: ${e.error}`));
                    }
                    setBulkAction(null);
                    setSelected(new Set());
                    fetchAll();
                  } catch (e) {
                    toast.error("Error: " + (e.response?.data?.detail || e.message));
                  } finally {
                    setBulkRunning(false);
                  }
                }}
                disabled={bulkRunning}
                className="px-4 py-2 text-sm font-bold text-white rounded-xl transition"
                style={{ background: "#DC2626" }}
                data-testid="bulk-delete-confirm"
              >
                {bulkRunning ? <Loader2 size={14} className="animate-spin inline mr-1" /> : <Trash2 size={14} className="inline mr-1" />}
                Eliminar {selected.size} usuario{selected.size !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatChip({ label, value, color = "#0A3747", bold = false }) {
  return (
    <div className="rounded-lg px-3 py-2" style={{ background: "#F8FAFC", border: `1px solid ${color}22` }}>
      <div className="text-[9px] uppercase tracking-widest font-semibold" style={{ color, opacity: 0.75 }}>{label}</div>
      <div className={`text-lg ${bold ? "font-extrabold" : "font-bold"} mt-0.5`} style={{ color }}>{value ?? 0}</div>
    </div>
  );
}

function Stat({ label, value, tone = "navy" }) {
  const styles = {
    navy:  { bg: "linear-gradient(145deg, #F1F5F9, #E4EAF0)",   fg: "#0A3747", ring: "rgba(163,177,198,0.55)" },
    green: { bg: "linear-gradient(145deg, #34D399, #10B981)",   fg: "#064E3B", ring: "rgba(16,185,129,0.5)" },
    cyan:  { bg: "linear-gradient(145deg, #93C5FD, #3B82F6)",   fg: "#1E3A8A", ring: "rgba(59,130,246,0.5)" },
    amber: { bg: "linear-gradient(145deg, #FCD34D, #F59E0B)",   fg: "#78350F", ring: "rgba(245,158,11,0.5)" },
    red:   { bg: "linear-gradient(145deg, #FCA5A5, #EF4444)",   fg: "#7F1D1D", ring: "rgba(239,68,68,0.5)" },
  };
  const st = styles[tone] || styles.navy;
  return (
    <div
      className="afc-appear rounded-2xl px-4 py-3 transition-all hover:-translate-y-0.5"
      style={{
        background: st.bg,
        boxShadow: `-6px -6px 14px rgba(255,255,255,0.75), 6px 6px 14px ${st.ring}, inset -1px -1px 3px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.5)`,
      }}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: st.fg, opacity: 0.75 }}>{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: st.fg, textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function ZoneMultiSelect({ branches, zones, selected, onChange, testIdPrefix }) {
  const toggle = (zoneId) => {
    if (selected.includes(zoneId)) onChange(selected.filter((z) => z !== zoneId));
    else onChange([...selected, zoneId]);
  };
  const grouped = branches.map((b) => ({
    branch: b,
    zones: zones.filter((z) => z.branch_id === b.id),
  }));
  const orphans = zones.filter((z) => !z.branch_id);

  if (zones.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic p-3 border border-gray-200 rounded bg-gray-50" data-testid={`${testIdPrefix}-zones-empty`}>
        No hay zonas configuradas. Ve a <span className="font-mono">Administración › Sucursales y Zonas</span>.
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded bg-white max-h-48 overflow-y-auto" data-testid={`${testIdPrefix}-zones-multiselect`}>
      {grouped.map(({ branch, zones: bZones }) => {
        if (bZones.length === 0) return null;
        return (
          <div key={branch.id} className="border-b border-gray-100 last:border-b-0">
            <div className="px-2.5 py-1 bg-gray-50 text-[10px] font-mono uppercase tracking-wider text-gray-600 font-semibold">
              {branch.name}
            </div>
            <div className="px-2 py-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
              {bZones.map((z) => (
                <label key={z.id} className="flex items-center gap-1.5 text-xs py-0.5 cursor-pointer hover:bg-emerald-50 rounded px-1">
                  <input
                    type="checkbox"
                    checked={selected.includes(z.id)}
                    onChange={() => toggle(z.id)}
                    className="w-3.5 h-3.5"
                    data-testid={`${testIdPrefix}-zone-${z.code}`}
                  />
                  <span className="truncate">{z.name}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
      {orphans.length > 0 && (
        <div>
          <div className="px-2.5 py-1 bg-amber-50 text-[10px] font-mono uppercase tracking-wider text-amber-800 font-semibold">
            Sin sucursal
          </div>
          <div className="px-2 py-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
            {orphans.map((z) => (
              <label key={z.id} className="flex items-center gap-1.5 text-xs py-0.5 cursor-pointer hover:bg-emerald-50 rounded px-1">
                <input
                  type="checkbox"
                  checked={selected.includes(z.id)}
                  onChange={() => toggle(z.id)}
                  className="w-3.5 h-3.5"
                  data-testid={`${testIdPrefix}-zone-${z.code}`}
                />
                <span className="truncate">{z.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {selected.length > 0 && (
        <div className="px-2.5 py-1.5 bg-emerald-50 text-[10px] text-emerald-800 border-t border-emerald-200">
          {selected.length} zona{selected.length !== 1 ? "s" : ""} seleccionada{selected.length !== 1 ? "s" : ""}
          <button type="button" onClick={() => onChange([])} className="ml-2 text-emerald-700 hover:underline font-medium">Limpiar</button>
        </div>
      )}
    </div>
  );
}
